/**
 * DWELL TIME — how long something waited.
 *
 * ── THESE ARE PERCENTILES AND THE COPY NEVER SAYS "AVERAGE" ──
 * Not a preference. One parcel forgotten on the shelf for six weeks moves a mean
 * by days and says nothing whatsoever about what the typical resident
 * experienced — and a mean is exactly the figure a desk manager would act on.
 * p50 is what the typical parcel experienced, p90 is the bad day, p99 is the
 * tail. The server sends no mean at all, deliberately, and no label, tooltip or
 * caption on this panel invents one.
 *
 * ── THE BUCKET LABELS COME OFF THE WIRE ──
 * Each subject ships its OWN edges (a parcel waits days, a visitor waits hours)
 * and each bucket ships its own `label`. Re-deriving "6-24h" in the browser from
 * two second counts is how an axis comes to disagree with the bars above it.
 *
 * ── THE TWO THRESHOLD NUMBERS ARE NOT THE SAME NUMBER ──
 * `breached` of `count` is a fact about the WINDOW. `overThreshold` of
 * `stillOpen` is a fact about RIGHT NOW. Different denominators, different
 * populations, rendered as two separate statements. Dividing one by the other's
 * denominator would make both sentences false.
 *
 * ── THE AGEING LIST CARRIES NO WINDOW, AND SAYS SO WHERE IT IS ──
 * The server proves this rather than claiming it: `stillOpen` is identical
 * across two different windows while `count` differs. So the list is captioned
 * as current ABOVE the rows, not only in the notes underneath them. If a reader
 * moves the range chips and this list does not move, the sentence explaining why
 * has to be one they have already read.
 */
import React, { useState } from 'react';
import { Bar, BarChart, CartesianGrid, ReferenceLine, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AnalyticsLatency,
  AnalyticsLatencySubject,
} from '../../../../../api/analytics-client';
import { dataViz, palette, typography } from '../../../../../style-utils/theme';
import { PanelShell, ChartShellState, ScrollableChart } from '../charts/chart-shell';
import { DashSimpleTooltip } from '../charts/chart-tooltip';
import { SectionEmpty } from '../charts/chart-empty';
import {
  DataTable,
  PanelToggle,
  TableScroller,
  Td,
  Th,
  ChartArea,
} from '../charts/chart.styled';
import {
  BAR_RADIUS,
  cursor,
  format,
  grid,
  margins,
  markSeparator,
  referenceLine,
  xAxisCategory,
  yAxisCount,
} from '../charts/chart-theme';
import { clockNote } from '../../../../../function-utils/building-time';
import { useClock } from '../../../../../context/clock-context';
import {
  FigureCell,
  FigureGrid,
  FigureLabel,
  FigureNote,
  FigureUnit,
  FigureValue,
  LiveDot,
  LiveTag,
  MeterMeta,
  PanelSection,
  PanelSectionHead,
  PanelSectionTitle,
  SectionCaption,
  StatePill,
} from './panels.styled';
import { hubNotes } from './panel-utils';
import { ResponsiveContainer } from 'recharts';
import { SpreadBar } from '../charts/spread-bar';

const PANEL_ID = 'dwell';

/** "a parcel", "an activity". The nouns come off the wire, so the article has to
 *  be derived rather than written into the sentence — the first browser pass on
 *  this panel read "Measured from when a activity was logged". */
const article = (noun: string): string =>
  /^[aeiou]/i.test(noun) ? `an ${noun}` : `a ${noun}`;

/**
 * THE VERB FOR THIS SUBJECT'S WORKLIST. A parcel WAITS on a shelf, a visitor is
 * INSIDE, an activity is OPEN. The server calls the figure `stillOpen` for all
 * three because it is one query, but "the activity that has been waiting
 * longest" is not a sentence anybody at a desk would write.
 */
const WAITING: Record<string, { heading: string; verb: string; column: string }> = {
  parcel: { heading: 'Waiting right now', verb: 'waiting', column: 'Waiting' },
  visitor: { heading: 'Still inside right now', verb: 'inside', column: 'Inside for' },
  activity: { heading: 'Still open right now', verb: 'open', column: 'Open for' },
};

const verbFor = (subject: string) =>
  WAITING[subject] || { heading: 'Still open right now', verb: 'open', column: 'Open for' };

const SUBJECT_CHIP: Record<string, string> = {
  parcel: 'Parcels',
  visitor: 'Visits',
  activity: 'Activities',
};

/** Which bucket a percentile falls in. Returned as the bucket's own LABEL,
 *  because that is what a categorical recharts axis matches on — and because the
 *  marker is then making the honest claim ("p50 is in this bucket") rather than
 *  pretending to a position inside it that the histogram cannot show. */
export const bucketOf = (
  histogram: AnalyticsLatency['histogram'],
  secs: number | null
): string | null => {
  if (secs == null) return null;
  const hit = histogram.find(
    bucket => secs >= bucket.from && (bucket.to == null || secs < bucket.to)
  );

  return hit ? hit.label : null;
};

export interface DwellPanelProps {
  state: ChartShellState;
  data?: AnalyticsLatency;
  subjects: AnalyticsLatencySubject[];
  subject: string;
  onSubject: (next: string) => void;
  onExportCsv?: () => void;
}

export const DwellPanel: React.FC<DwellPanelProps> = ({
  state,
  data,
  subjects,
  subject,
  onSubject,
  onExportCsv,
}) => {
  const [showTable, setShowTable] = useState(false);

  const extra: string[] = [];
  if (data) {
    if (data.ageingTruncated) {
      extra.push(
        `The ageing list is capped at ${data.ageing.length} rows; there are ` +
          `${data.stillOpen.toLocaleString()} still open in total, so the oldest ` +
          'are shown and the rest are counted but not listed.'
      );
    }
    // THE HONEST NULL. `activity` declares agreesWith: null because no breakdown
    // measure counts activity closes. An indicator pointing at a near-miss would
    // be worse than none, so the absence is stated rather than filled in.
    extra.push(
      data.agreesWith
        ? `This count of ${data.noun[1]} closed in the period must equal the ` +
            `"${data.agreesWith}" figure on the count panels above. If the two ` +
            'disagree, one of them is wrong.'
        : `There is no count panel that measures ${data.noun[1]} being closed, so ` +
            'this figure has nothing to be cross-checked against. That is stated ' +
            'rather than compared against something close to it.'
    );
  }
  const notes = data ? hubNotes(data, extra) : [];

  // A subject with nothing closed AND nothing waiting is the only genuinely
  // empty state. Either one alone is a real answer, and the sections below
  // render them independently.
  const wholeEmpty =
    !!data && data.count === 0 && data.stillOpen === 0 && !data.ageing.length;

  const subjectLabel =
    subjects.find(item => item.key === subject)?.label || data?.label || '';

  /* THE CLOCK THE TIMESTAMPS ARE ON, STATED IN THE COLUMN HEADER — the
     answer's own (`tz`, R-T12): the reader's for an owner or a manager, whatever
     zones the buildings are in. It was the one zone in scope, and with several
     it silently became UTC. */
  const clock = useClock({ response: data });
  const tz = clock.zone;
  const p50Bucket = data ? bucketOf(data.histogram, data.p50) : null;
  const p90Bucket = data ? bucketOf(data.histogram, data.p90) : null;

  return (
    <PanelShell
      id={PANEL_ID}
      title={`Dwell time — ${subjectLabel}`}
      wide
      state={state}
      notes={notes}
      onExportCsv={onExportCsv}
      caveat={
        data
          ? /* SHORT ON PURPOSE. The server's own note underneath already explains
               at length why there is no mean here; repeating it in the header is
               the duplicated-copy defect the browser pass found four times. */
            `Measured from when ${article(data.noun[0])} was ${data.measuredFrom} to when it was ${data.measuredTo}, for the ones that closed inside this period. The three figures are percentiles.`
          : 'How long something waited, as percentiles.'
      }
      emptyReason={
        wholeEmpty
          ? {
              title: `No ${data?.noun?.[1] || 'items'} closed, and none are waiting`,
              text:
                'Nothing closed inside this period and nothing is open right now, so ' +
                'there is no distribution and no worklist. A quiet period is a normal ' +
                'answer.',
            }
          : null
      }
      controls={
        <>
          {/* THE SUBJECT SWITCHER, BUILT FROM META, and in the URL — so a
              colleague sent this link sees the subject that was being talked
              about rather than the default. */}
          {subjects.map(item => (
            <PanelToggle
              key={item.key}
              type="button"
              active={item.key === subject}
              aria-pressed={item.key === subject}
              data-testid={`dwell-subject-${item.key}`}
              onClick={() => onSubject(item.key)}
            >
              {SUBJECT_CHIP[item.key] || item.label}
            </PanelToggle>
          ))}
          {data && data.count > 0 ? (
            <PanelToggle
              type="button"
              active={showTable}
              aria-pressed={showTable}
              onClick={() => setShowTable(value => !value)}
            >
              {showTable ? 'Chart' : 'Table'}
            </PanelToggle>
          ) : null}
        </>
      }
    >
      {data ? (
        <>
          {/* ── the three percentiles ── */}
          {data.count > 0 ? (
            <PanelSection>
              <FigureGrid data-testid="dwell-percentiles">
                {[
                  {
                    key: 'p50',
                    label: 'Typical (p50)',
                    hours: data.hoursView.p50,
                    secs: data.p50,
                    note: `Half of the ${data.noun[1]} that closed waited less than this.`,
                  },
                  {
                    key: 'p90',
                    label: 'Slow (p90)',
                    hours: data.hoursView.p90,
                    secs: data.p90,
                    note: 'Nine in ten waited less than this. This is the bad day.',
                  },
                  {
                    key: 'p99',
                    label: 'Tail (p99)',
                    hours: data.hoursView.p99,
                    secs: data.p99,
                    note: 'The worst hundredth. One long-forgotten item lives here.',
                  },
                ].map(figure => {
                  /* THE SAME NUMBER IS NEVER PRINTED TWICE. formatSeconds and the
                     server's hoursView agree exactly in the one-to-24-hour band —
                     both say "17.2 h" — and the first browser pass rendered
                     "17.2 hours  17.2 h · Half of the parcels…". The second
                     reading earns its place only when it is a DIFFERENT reading:
                     minutes for a short visit, days for a forgotten parcel. */
                  const precise = format.seconds(figure.secs);
                  const alsoWorthSaying =
                    figure.hours != null &&
                    precise !== `${figure.hours.toLocaleString()} h`;

                  return (
                    <FigureCell key={figure.key} data-testid={`dwell-${figure.key}`}>
                      <FigureLabel>{figure.label}</FigureLabel>
                      {/* HOURS FROM THE SERVER'S OWN hoursView, not from a
                          division in the browser. A client that divides by 3600
                          itself is a client that will one day divide by 60. */}
                      <FigureValue>
                        {figure.hours == null ? '—' : figure.hours.toLocaleString()}
                        <FigureUnit>{figure.hours == null ? '' : 'hours'}</FigureUnit>
                      </FigureValue>
                      <FigureNote>
                        {alsoWorthSaying ? `${precise} · ` : ''}
                        {figure.note}
                      </FigureNote>
                    </FigureCell>
                  );
                })}
              </FigureGrid>

              {/* THE SAME THREE FIGURES, ON ONE AXIS.
                  Three numbers side by side are three numbers: nothing about
                  "17.2, 50, 111" says the middle of the distribution is tight
                  and the tail is enormous, which is the finding — and which the
                  same three make obvious the moment they are drawn on one line.
                  The whisker is the OBSERVED p50-to-p90 range, not a margin of
                  error, and the tooltip says so. */}
              {data.hoursView.p50 != null && data.hoursView.p90 != null ? (
                <ChartArea $aspect={6} $minHeight={110} data-testid="dwell-spread">
                  <ResponsiveContainer width="100%" height="100%">
                    <SpreadBar
                      rows={[
                        {
                          key: 'dwell',
                          label: 'Wait',
                          value: data.hoursView.p50,
                          range: [
                            0,
                            Math.max(0, data.hoursView.p90 - data.hoursView.p50),
                          ],
                          p50: data.hoursView.p50,
                          p90: data.hoursView.p90,
                          p99: data.hoursView.p99,
                          unit: 'hours',
                        },
                      ]}
                    />
                  </ResponsiveContainer>
                </ChartArea>
              ) : null}

              <MeterMeta>
                <span>
                  {data.count.toLocaleString()}{' '}
                  {data.count === 1 ? data.noun[0] : data.noun[1]} closed in this
                  period.
                </span>
                {/* THE THRESHOLD IS NAMED FROM THE RESPONSE. A number typed here
                    would be a second copy of a registry value, and the day
                    somebody moves the line the panel would go on quoting the old
                    one with complete confidence. */}
                <StatePill
                  tone={data.breached > 0 ? 'warning' : 'neutral'}
                  data-testid="dwell-breached"
                >
                  {data.breached.toLocaleString()} took longer than {data.thresholdHours}h
                  {data.breachedRatio == null ? '' : ` (${format.ratio(data.breachedRatio)})`}
                </StatePill>
                {data.machineClosed > 0 ? (
                  <StatePill tone="neutral" data-testid="dwell-machine-closed">
                    {data.machineClosed.toLocaleString()} closed by the nightly sweep,
                    not by a person
                  </StatePill>
                ) : null}
              </MeterMeta>
            </PanelSection>
          ) : (
            <SectionEmpty
              title={`Nothing was ${data.measuredTo} in this period`}
              text={
                'There is no distribution to report for this period. That is not the ' +
                'same as nothing having waited — the list below is what is open right ' +
                'now.'
              }
            />
          )}

          {/* ── the histogram ── */}
          {data.count > 0 ? (
            <PanelSection>
              <PanelSectionHead>
                <PanelSectionTitle>How the wait was distributed</PanelSectionTitle>
                <SectionCaption>
                  Height is how many {data.noun[1]} ended up in each bucket. The edges
                  and their labels come from the server, and each subject has its own.
                </SectionCaption>
              </PanelSectionHead>
              {showTable ? (
                <TableScroller>
                  <DataTable data-testid="dwell-table">
                    <thead>
                      <tr>
                        <Th scope="col">Wait</Th>
                        <Th scope="col" numeric>
                          {data.noun[1]}
                        </Th>
                        <Th scope="col" numeric>
                          Share
                        </Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.histogram.map(bucket => (
                        <tr key={bucket.label}>
                          <Td>{bucket.label}</Td>
                          <Td numeric>{bucket.count.toLocaleString()}</Td>
                          <Td numeric>{format.share(bucket.count, data.count)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </TableScroller>
              ) : (
                <ScrollableChart>
                  <ChartArea $aspect={3} $minHeight={220} $minWidth={520}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart accessibilityLayer
                        data={data.histogram}
                        margin={margins.withTopLabels}
                        barCategoryGap="18%"
                      >
                        <CartesianGrid {...grid} />
                        {/* THE SERVER'S OWN LABEL as the category. */}
                        <XAxis dataKey="label" {...xAxisCategory} interval={0} />
                        <YAxis {...yAxisCount} />
                        <Tooltip
                          content={
                            <DashSimpleTooltip
                              tone={dataViz.series[0]}
                              valueKey="count"
                              labelKey="label"
                              noun={data.noun}
                              total={data.count}
                              note={row =>
                                `${format.seconds(row.from as number)}${
                                  row.to == null
                                    ? ' and over'
                                    : ` to ${format.seconds(row.to as number)}`
                                }`
                              }
                            />
                          }
                          cursor={cursor.bar}
                        />
                        <Bar
                          dataKey="count"
                          fill={dataViz.series[0]}
                          radius={BAR_RADIUS}
                          {...markSeparator}
                          isAnimationActive={false}
                        />
                        {/* WHERE p50 AND p90 FALL ON THE AXIS.
                            DECLARED AFTER THE BAR, and that ordering is the whole
                            fix from the first browser pass: recharts paints in
                            child order, so a marker declared before the bars was
                            drawn UNDER them — and p50 lands in the tallest bucket
                            by definition, which is exactly the bar that hid it. */}
                        {p50Bucket ? (
                          <ReferenceLine
                            x={p50Bucket}
                            {...referenceLine}
                            stroke={palette.inkSoft}
                            label={{
                              value: 'p50',
                              position: 'top',
                              fill: palette.inkSoft,
                              fontSize: typography.size.xxs,
                              fontFamily: typography.ui,
                            }}
                          />
                        ) : null}
                        {p90Bucket ? (
                          <ReferenceLine
                            x={p90Bucket}
                            {...referenceLine}
                            stroke={palette.slate}
                            label={{
                              value: 'p90',
                              position: 'top',
                              fill: palette.slate,
                              fontSize: typography.size.xxs,
                              fontFamily: typography.ui,
                            }}
                          />
                        ) : null}
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartArea>
                </ScrollableChart>
              )}
            </PanelSection>
          ) : null}

          {/* ── the ageing worklist ── */}
          <PanelSection>
            <PanelSectionHead>
              <PanelSectionTitle>
                {verbFor(data.subject).heading}, oldest first
              </PanelSectionTitle>
              <LiveTag data-testid="ageing-live-tag">
                <LiveDot aria-hidden="true" />
                as of now
              </LiveTag>
            </PanelSectionHead>
            {/* ABOVE THE ROWS, NOT UNDER THEM. This list has no window at all —
                the server proves it by returning the same stillOpen for two
                different windows — so changing the range will not move it. A
                reader who discovers that by experiment concludes the screen is
                broken. */}
            <SectionCaption data-testid="ageing-caption">
              This list is <strong>not filtered by the period above</strong>. It is what
              is open at this moment, oldest first — the {data.noun[0]} that has been{' '}
              {verbFor(data.subject).verb} longest is almost always outside whatever
              window you are looking at, and it is the only row worth chasing.
            </SectionCaption>
            <MeterMeta>
              <span>{data.stillOpen.toLocaleString()} open now.</span>
              <StatePill
                tone={data.overThreshold > 0 ? 'warning' : 'neutral'}
                data-testid="ageing-over-threshold"
              >
                {data.overThreshold.toLocaleString()} of those already past{' '}
                {data.thresholdHours}h
              </StatePill>
            </MeterMeta>
            {data.ageing.length ? (
              <TableScroller>
                <DataTable data-testid="ageing-table">
                  <thead>
                    <tr>
                      <Th scope="col">Building</Th>
                      <Th scope="col">Unit</Th>
                      <Th scope="col">Since ({clockNote(clock)})</Th>
                      <Th scope="col" numeric>
                        {verbFor(data.subject).column}
                      </Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ageing.map(row => (
                      <tr key={`${row.buildingId}-${row.id}`}>
                        <Td>{row.building}</Td>
                        {/* '' IS AN ORDINARY STATE. The unit is free courier text
                            on a parcel and free desk text on a log row, so
                            "nobody wrote one down" is not a missing join and must
                            not read as an error. */}
                        <Td>{row.unit || 'not recorded'}</Td>
                        <Td>{format.instant(row.at, tz)}</Td>
                        <Td numeric>
                          {format.seconds(row.ageSeconds)}
                          {row.overThreshold ? (
                            <>
                              {' '}
                              <StatePill tone="warning">over</StatePill>
                            </>
                          ) : null}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </TableScroller>
            ) : (
              <SectionEmpty
                title={`Nothing is ${verbFor(data.subject).verb}`}
                text={`No ${data.noun[0]} is open at this moment, so there is nothing on the worklist. That is the good answer.`}
              />
            )}
          </PanelSection>
        </>
      ) : null}
    </PanelShell>
  );
};
